<script setup lang="ts" vapor>
import { BaseObjListView, DisplayText, DropdownComp } from '@/components';
import { BibAssign, StartingNumber } from '@/objects/generated';
import {
    useBibAssignStore,
    useBibAssignStoreRef,
    useCommonStore,
    useContestantStore,
    useContestantStoreRef,
    useEventCommonStoreRef,
    useEventStore,
    useGuiStore,
    useToastStore,
} from '@/stores';
import { computed, ref, watch } from 'vue';
import { DropdownItemButton, DropdownItemCheckbox, DropdownItemMenu, DropdownItemSeparator } from '@/objects';
import { BibAssignDao } from '@/dao';
import { useLoaders, useTaskLoader } from '@/composeables';
import BibAssignSeedingGroup from '../BibAssign/BibAssignSeedingGroup.vue';
import BibAssignDialog from '../BibAssign/BibAssignDialog.vue';
import type { GridColumnOptions } from '@/components/Grid/Classes';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import { arrayUnique, OpenContestantModal, propertyof } from '@/utilities';
import type BaseObjListViewCompExpose from '@/objects/BaseObjListViewCompExpose';
import { useBibsActions } from '@/composeables/bibsActions';
import ShowOrAddContestant from './ShowOrAddContestant.vue';
import { CreateNumbersFromPattern } from '@/utilities/searchutils';
import { $tm } from '@/locize';
import { useDialogBuilder } from '@/composeables/dialog/dialogbuilder';

const guiStore = useGuiStore();

const grid = ref<BaseObjListViewCompExpose<StartingNumber> | null>(null);

const nameGetter = (bib: StartingNumber) => {
    return bib.Bib.toString();
};

const { BibAssigns } = useBibAssignStoreRef();
const { CategoriesMap, GroupsMap } = useEventCommonStoreRef();
const Bibs = ref<Array<StartingNumber>>([]);

const PossibleBibAssignValue = computed<Map<number | null, string>>(() => {
    const map = new Map<number | null, string>();
    BibAssigns.value.forEach((ba: BibAssign) => {
        const val = ba.CategoryID
            ? CategoriesMap.value.get(ba.CategoryID)?.Name
            : ba.GroupID
              ? GroupsMap.value.get(ba.GroupID)?.Name
              : $tm('general.all');
        map.set(ba.Bib, val || '');
    });
    return map;
});

// Ref to the modal
const bibAssignDialog = ref<InstanceType<typeof BibAssignDialog> | null>(null);

const { AddLoader, LoadState } = useTaskLoader();
useLoaders(AddLoader, ['CONTESTANTS', 'BIBASSIGN']);

const commonStore = useCommonStore();

const assignActions = [
    DropdownItemButton.create({
        buttonType: 'Custom',
        action: () => {
            bibAssignDialog.value?.show();
        },
        icon: 'fa-hat-wizard',
        translationkey: 'bibassign.open_wizard',
    }),
    DropdownItemButton.create({
        buttonType: 'Custom',
        action: () => {
            seedinggroupview.value?.show();
        },
        icon: 'fa-list-ol',
        translationkey: 'bibassign.open_assign_seeding_groups',
    }),
    DropdownItemButton.create({
        buttonType: 'Custom',
        action: async () => {
            const resp = await BibAssignDao.BibAssignBuild(useEventStore().eventID, true, false, false);
            if (resp?.Success) {
                useToastStore().addSuccess(resp.TranslationKey ?? 'bibassign.assigned');
                useBibAssignStore().ReloadBibAssigns(); // assigned below will await the reload so no reason to await here
                assigned();
            } else {
                useToastStore().addError(resp?.TranslationKey ?? 'bibassign.error');
            }
        },
        icon: 'fa-calendar-check',
        translationkey: 'bibassign.assign',
    }),
];

const actions = [
    DropdownItemSeparator.create(),
    ...assignActions,
    DropdownItemSeparator.create(),
    /* contestant actions */
    DropdownItemButton.create({
        action: () => unassignContestantsOnSelected(),
        icon: 'fa-circle-minus',
        translationkey: 'bibs.unassign_selected_bibs',
    }),
    DropdownItemSeparator.create(),

    /* create actions */
    DropdownItemMenu.create({
        translationkey: 'bibs.add_bibs',
        icon: 'fa-plus',
        dropdownItems: [
            DropdownItemButton.create({
                action: () => createBibsFromCategoriesOrGroups(),
                icon: 'fa-plus',
                translationkey: 'bibs.create_bibs_from_categories_or_groups',
            }),
            DropdownItemButton.create({
                action: () => createBibsFromTo(),
                icon: 'fa-plus',
                translationkey: 'bibs.create_bibs_from_to',
            }),
        ],
    }),

    /* starttime actions */
    DropdownItemMenu.create({
        translationkey: 'bibs.starttime_actions',
        icon: 'fa-clock',
        dropdownItems: [
            DropdownItemButton.create({
                action: () => removeStartTimeOnSelected(),
                icon: 'fa-clock',
                translationkey: 'bibs.remove_starttime_selected_bibs',
            }),
            DropdownItemButton.create({
                action: () => moveStartTimesOnSelected(),
                icon: 'fa-clock-rotate-left',
                translationkey: 'bibs.move_starttimes_on_selected_bibs',
            }),
            DropdownItemButton.create({
                action: () => setStartTimeOnSelected(),
                icon: 'fa-clock',
                translationkey: 'bibs.set_starttime_selected_bibs',
            }),
            // DropdownItemButton.create({
            //     action: () => copyStartTimeToContestants(),
            //     icon: 'fa-copy',
            //     translationkey: 'bibs.copy_starttime_to_contestant',
            // }),
            DropdownItemButton.create({
                action: () => copyStartTimeFromContestants(),
                icon: 'fa-copy',
                translationkey: 'bibs.copy_starttime_from_contestant',
            }),
            DropdownItemButton.create({
                action: () => uploadFileWithStartTimes(),
                icon: 'fa-file',
                translationkey: 'bibs.upload_file_with_starttimes',
            }),
        ],
    }),

    /* chip actions */
    DropdownItemMenu.create({
        translationkey: 'bibs.chip_actions',
        icon: 'fa-square-a',
        dropdownItems: [
            DropdownItemButton.create({
                action: () => assignChipOnSelectedWhenMissingchip(),
                icon: 'fa-square-a',
                translationkey: 'bibs.assign_chip_to_selected_bibs_missing_chip',
            }),
            DropdownItemButton.create({
                action: () => assignChipOnSelected(),
                icon: 'fa-square-a',
                translationkey: 'bibs.assign_chip_to_selected_bibs',
            }),
            DropdownItemButton.create({
                action: () => setChipAsOffsetFromBibOnSelected(),
                icon: 'fa-arrow-right-arrow-left',
                translationkey: 'bibs.set_chip_as_offset_from_bib',
            }),
            DropdownItemButton.create({
                action: () => removeChipFromSelected(),
                icon: 'fa-xmark',
                translationkey: 'bibs.remove_chip_from_selected',
            }),
            DropdownItemButton.create({
                action: () => setEmitTagOnSelected(),
                icon: 'fa-square-a',
                translationkey: 'bibs.set_emittag_to_selected',
            }),
            DropdownItemButton.create({
                action: () => uploadFileWithChips(),
                icon: 'fa-file',
                translationkey: 'bibs.upload_file_with_chips',
            }),
        ],
    }),

    /* block/unblock actions */
    DropdownItemMenu.create({
        translationkey: 'bibs.block_unblock_actions',
        icon: 'fa-lock',
        dropdownItems: [
            DropdownItemButton.create({
                action: () => blockSelected(),
                icon: 'fa-circle-minus',
                translationkey: 'bibs.block_selected_bibs',
            }),
            DropdownItemButton.create({
                action: () => unblockSelected(),
                icon: 'fa-circle-check',
                translationkey: 'bibs.unblock_selected_bibs',
            }),
        ],
    }),

    /* lane actions */
    DropdownItemMenu.create({
        translationkey: 'bibs.lane_actions',
        icon: 'fa-bullseye',
        dropdownItems: [
            DropdownItemButton.create({
                action: () => removeLaneOnSelected(),
                icon: 'fa-bullseye',
                translationkey: 'bibs.remove_lane_selected_bibs',
            }),
            DropdownItemButton.create({
                action: () => assignLaneToSelected(),
                icon: 'fa-bullseye',
                translationkey: 'bibs.assign_lane_selected_bibs',
            }),
        ],
    }),

    // flagactions
    DropdownItemMenu.create({
        translationkey: 'contestant.setdnx',
        icon: 'fa-badge-check',
        dropdownItems: [
            DropdownItemButton.create({
                translationkey: 'contestant.setdns',
                action: () => setExitStatus('DNS'),
            }),
            DropdownItemButton.create({
                translationkey: 'contestant.setdnf',
                action: () => setExitStatus('DNF'),
            }),
            DropdownItemButton.create({
                translationkey: 'contestant.setdsq',
                action: () => setExitStatus('DSQ'),
            }),
            DropdownItemButton.create({
                translationkey: 'contestant.setlap',
                action: () => setExitStatus('LAP'),
            }),
            DropdownItemButton.create({
                translationkey: 'contestant.setdnq',
                action: () => setExitStatus('DNQ'),
            }),
            DropdownItemButton.create({
                translationkey: 'contestant.setfinished',
                action: () => setExitStatus('Finished'),
            }),

            DropdownItemSeparator.create(),

            DropdownItemButton.create({
                translationkey: 'contestant.removealldnx',
                action: () => setExitStatus('None'),
            }),
        ],
    }),

    DropdownItemMenu.create({
        translationkey: 'bibs.assign_to_contestants',
        icon: 'fa-calendar-check',
        dropdownItems: [
            DropdownItemButton.create({
                action: () => uploadFileWithParticipants(),
                translationkey: 'bibs.upload_file_with_participants',
            }),
        ],
    }),

    DropdownItemButton.create({
        translationkey: 'bibs.uploadfiles',
        icon: 'fa-file-import',
        action: () => uploadFileActions('bibs.uploadfiles', 'bibs.uploadfiles_info'),
    }),
];

// Bibs actions composable
const bibsActions = useBibsActions(grid, Bibs);
const setStartTimeOnSelected = bibsActions.setStartTimeOnSelected;
const assignLaneToSelected = bibsActions.assignLaneToSelected;
const removeStartTimeOnSelected = bibsActions.removeStartTimeOnSelected;
const assignChipOnSelectedWhenMissingchip = bibsActions.assignChipOnSelectedWhenMissingchip;
const assignChipOnSelected = bibsActions.assignChipOnSelected;
const moveStartTimesOnSelected = bibsActions.moveStartTimesOnSelected;
const setChipAsOffsetFromBibOnSelected = bibsActions.setChipAsOffsetFromBibOnSelected;
const removeChipFromSelected = bibsActions.removeChipFromSelected;
const unassignContestantsOnSelected = bibsActions.unassignContestantsOnSelected;
const removeLaneOnSelected = bibsActions.removeLaneOnSelected;
const blockSelected = bibsActions.blockSelected;
const unblockSelected = bibsActions.unblockSelected;
const setEmitTagOnSelected = bibsActions.setEmitTagOnSelected;
const copyStartTimeToContestants = bibsActions.copyStartTimeToContestants;
const copyStartTimeFromContestants = bibsActions.copyStartTimeFromContestants;
const setExitStatus = bibsActions.setExitStatus;
const createBibsFromCategoriesOrGroups = bibsActions.createBibsFromCategoriesOrGroups;
const createBibsFromTo = bibsActions.createBibsFromTo;
const uploadFileWithStartTimes = bibsActions.uploadFileWithStartTimes;
const uploadFileWithParticipants = bibsActions.uploadFileWithParticipants;
const uploadFileWithChips = bibsActions.uploadFileWithChips;
const uploadFileActions = bibsActions.uploadFileActions;

const seedinggroupview = ref<InstanceType<typeof BibAssignSeedingGroup> | null>(null);

/**
 * This is called when the bibs have been assigned and published.
 */
const assigned = () => {
    bibAssignDialog.value?.hide();
    refresh();
};

/**
 * Refresh the bib list
 */
const refresh = async () => {
    AddLoader(async () => {
        // Reload the bibassigns and refresh the grid
        await useBibAssignStore().EnsureLoaded(['BibAssign']);
        // Refresh the grid
        grid.value?.refreshList();
    }, 'bibassign.loading');
};

const addNewContestant = async (row: StartingNumber) => {
    OpenContestantModal({
        preFillBib: row.Bib,
        prefillStartTime: row.StartTime ? new Date(row.StartTime) : undefined,
        openParticipantSearch: true,
        onContestantAdded: async () => {
            await grid.value?.refreshList([row.ID]);
        },
    });
};

const addCustomColumns = (columns: GridColumnOptions<StartingNumber>[]) => {
    new GridColumnOptionsBuilder<StartingNumber, number | null>(propertyof<StartingNumber>('ContestantID'), 'startingnumber.contestant')
        .ReadOnly()
        .SortDisabled()
        .CellRenderer(ShowOrAddContestant, {
            datasource: { mapsource: useContestantStoreRef().ContestantFullNameWithEmptyAsSelectLine },
            addAction: addNewContestant,
        })
        .Insert(columns, 1);

    new GridColumnOptionsBuilder<StartingNumber, number>(
        propertyof<StartingNumber>('Bib'),
        'startingnumber.possible_category_or_group',
        'possible_category_or_group',
    )
        .ReadOnly()
        .SortDisabled()
        .ValueGetter((row: StartingNumber) => {
            return PossibleBibAssignValue.value.get(row.Bib) ?? '';
        })
        .Filterable(() => {
            const un = arrayUnique(
                BibAssigns.value.map((ba) =>
                    ba.CategoryID
                        ? CategoriesMap.value.get(ba.CategoryID)!.Name
                        : ba.GroupID
                          ? GroupsMap.value.get(ba.GroupID)!.Name
                          : $tm('general.all'),
                ),
            );
            return new Map<string, any>(un.map((x) => [x.toString(), x]));
        })
        .InsertAtEnd(columns);

    // We need a columnfor contestant status
    new GridColumnOptionsBuilder<StartingNumber, number | null>(
        propertyof<StartingNumber>('ContestantID'),
        'startingnumber.contestant_status',
        propertyof<StartingNumber>('ContestantID') + '_contestant_status',
    )
        .ReadOnly()
        .SortDisabled()
        .CellRenderer(DisplayText)
        .ValueGetter((row: StartingNumber) => {
            if (!row.ContestantID) {
                return '';
            }
            const contestant = useContestantStore().GetContestantById(row.ContestantID);
            if (!contestant) {
                return '';
            }

            return $tm(commonStore.ContestantStatus.get(contestant.ContestantStatusID)?.text ?? '');
        })
        .InsertAtEnd(columns);

    // We need a column for seeding

    // Column for utlevert

    return columns;
};

const filterListOnSearch = (bib: StartingNumber, searchstring: string): boolean => {
    if (searchstring.trim() === '') {
        return true;
    }
    const range = CreateNumbersFromPattern(searchstring);
    if (range.size > 0) {
        return range.has(bib.Bib);
    }
    if (bib.ContestantID) {
        const contestant = useContestantStore().GetContestantById(bib.ContestantID);
        if (contestant) {
            const fullname = (contestant.Firstname?.toLowerCase() ?? '') + ' ' + (contestant.Surname?.toLowerCase() ?? '');
            if (!fullname.includes(searchstring.toLowerCase())) {
                return false;
            }
        }
    }
    const categoryOrGroup = PossibleBibAssignValue.value.get(bib.Bib) ?? '';
    if (categoryOrGroup != '' && !categoryOrGroup.toLowerCase().includes(searchstring.toLowerCase())) {
        return false;
    }

    return true;
};

const ShowOnlyAvailableStartNumbers = ref<boolean>(false);
const ShowOnlyWithoutChip1 = ref<boolean>(false);
const ShowOnlyWithoutChip2 = ref<boolean>(false);
const ShowOnlyUsedStartNumbers = ref<boolean>(false);

watch(
    () => [ShowOnlyAvailableStartNumbers.value, ShowOnlyWithoutChip1.value, ShowOnlyWithoutChip2.value, ShowOnlyUsedStartNumbers.value],
    () => {
        grid.value?.refreshList();
    },
);

const dropdownItems = [
    DropdownItemCheckbox.create({ label: 'bibs.show_only_available_startnumbers', modelValue: ShowOnlyAvailableStartNumbers }),
    DropdownItemCheckbox.create({ label: 'bibs.show_only_without_chip1', modelValue: ShowOnlyWithoutChip1 }),
    DropdownItemCheckbox.create({ label: 'bibs.show_only_without_chip2', modelValue: ShowOnlyWithoutChip2 }),
    DropdownItemCheckbox.create({ label: 'bibs.show_only_used_startnumbers', modelValue: ShowOnlyUsedStartNumbers }),
];

const filterBibs = (bib: StartingNumber): boolean => {
    if (ShowOnlyAvailableStartNumbers.value && bib.ContestantID) {
        return false;
    }
    if (ShowOnlyUsedStartNumbers.value && !bib.ContestantID) {
        return false;
    }
    if (ShowOnlyWithoutChip1.value && bib.Chip1) {
        return false;
    }
    if (ShowOnlyWithoutChip2.value && bib.Chip2) {
        return false;
    }
    return true;
};

/**
 * Handling save of the lsit. We want to show a modal with some info on what happens with the starting times
 */
const onSaveList = async () => {
    const changed = Bibs.value.filter((o: StartingNumber) => o.EntityState != 'Unchanged');

    if (changed.length === 0) {
        useToastStore().addInfo('bibs.nosavedchanges');
        return;
    }

    const { AddLoader: AddLoaderSaveList, LoadState: LoadStateSaveList } = useTaskLoader();

    useDialogBuilder()
        .withTitle('bibs.save_changes')
        .withBodyText('bibs.save_changes_info')
        .withCancelButton({
            action: () => {},
        })
        .withSaveButton({
            loadstate: LoadStateSaveList,
            action: async () => {
                await AddLoaderSaveList(async () => {
                    try {
                        await grid.value?.saveList();
                    } catch (error) {
                        useToastStore().addError('bibs.save_changes_error');
                    }
                }, '');

                return true;
            },
        })
        .withOptions({ Size: 'md' })
        .Open();
};
</script>
<template>
    <div class="h-100 overflow-auto">
        <BaseObjListView
            :base-type="StartingNumber"
            loading-text="bibs.loadingbibs"
            loading-icon="fa-user-check"
            pluralname="bibs.bibs"
            :name-getter="nameGetter"
            v-model="Bibs"
            :gui="guiStore.StartingNumber"
            :menuActions="actions"
            :quickButtons="assignActions"
            gridkey="bibsgridview"
            :customColumns="addCustomColumns"
            ref="grid"
            :filter="[filterListOnSearch, filterBibs]"
            :emitOnlyOnSave="true"
            @saveList="onSaveList"
        >
            <template #menu-filters>
                <DropdownComp
                    :dropdownItems="dropdownItems"
                    translationkey="general.filter"
                ></DropdownComp>
            </template>
        </BaseObjListView>

        <BibAssignDialog
            ref="bibAssignDialog"
            @assigned="assigned"
        />
        <BibAssignSeedingGroup ref="seedinggroupview" />
    </div>
</template>
